import { useMutation, useQueryClient } from "@tanstack/react-query";

import { supabase } from "~/integrations/supabase/client";

const BATCH_KEY = "production_batches";

interface CorrectBatchConsumptionInput {
  pbiId: string; // production_batch_ingredients id
  batchId: string; // production_batches id (for cache invalidation)
  newQuantity: number;
  reason: string;
}

/**
 * Correct an ingredient's actual consumption on a completed lote.
 *
 * Backed by the `correct_batch_ingredient_consumption` RPC, which restores /
 * deducts the stock delta on the batch's plant, updates the line's
 * actual/planned quantity, recomputes the batch actual cost, and re-syncs the
 * costing ledger. It only works on `completed` batches in an OPEN costing
 * period and is gated server-side to superadmin / admin / produccion-admin.
 *
 * The RPC is not in the generated Supabase types, so it is called through an
 * untyped handle.
 */
export function useCorrectBatchConsumption() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      pbiId,
      newQuantity,
      reason,
    }: CorrectBatchConsumptionInput) => {
      const { data, error } = await (
        supabase as unknown as {
          rpc: (
            fn: string,
            args: Record<string, unknown>
          ) => Promise<{ data: any; error: any }>;
        }
      ).rpc("correct_batch_ingredient_consumption", {
        p_pbi_id: pbiId,
        p_new_quantity: newQuantity,
        p_reason: reason,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: (_res, { batchId }) => {
      // Batch detail page query keys (see useProductionBatches.ts)
      queryClient.invalidateQueries({ queryKey: [BATCH_KEY] });
      queryClient.invalidateQueries({ queryKey: [BATCH_KEY, batchId] });
      queryClient.invalidateQueries({
        queryKey: [BATCH_KEY, batchId, "ingredient_costs"],
      });
      // Stock + costing surfaces affected by the correction
      queryClient.invalidateQueries({ queryKey: ["oil_types"] });
      queryClient.invalidateQueries({ queryKey: ["raw_materials"] });
      queryClient.invalidateQueries({ queryKey: ["oil_type_stock_by_plant"] });
      queryClient.invalidateQueries({
        queryKey: ["raw_material_stock_by_plant"],
      });
      queryClient.invalidateQueries({ queryKey: ["costing"] });
    },
  });
}
